'use client';

/**
 * MediaRenderer — single component dispatching the five media kinds.
 *
 * Cards never know which format they got. They pass a stable
 * `mediaRef` slug; this renderer fetches the validated MediaResource
 * via the `media_resource` BFF strategy and dispatches on `.kind`.
 *
 * The format question (MP4 vs SVG vs Lottie vs React component) is
 * decoupled from the card. Replacing a video with a Lottie animation
 * is a one-line YAML edit; the card spec is unchanged.
 *
 * @see klyde_edge/klyde_web_content/schemas.py — MediaResource shape
 * @see klyde_edge/klyde_web_content/data/media.yaml — entries
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain media
 */

import { useEffect, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { cn } from '@/lib/utils/cn';
import type { WebMediaResource } from '@/lib/api/types';
import { REACT_MEDIA_REGISTRY } from './react-media-registry';

interface MediaRendererProps {
  mediaRef: string;
  /** Optional className for the wrapping element (sizing / aspect-ratio). */
  className?: string;
}

export function MediaRenderer({ mediaRef, className }: MediaRendererProps) {
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', 'media_resource'),
  );

  const [data, setData] = useState<WebMediaResource | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebMediaResource>(strategy, { ref: mediaRef })
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || `Failed to load media ${mediaRef}`);
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, mediaRef]);

  if (error) {
    return (
      <div className={cn('flex flex-col items-center justify-center text-muted-foreground', className)}>
        <ImageOff className="h-6 w-6 opacity-40 mb-2" />
        <span className="text-xs">{error}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={cn('flex items-center justify-center text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return <DispatchByKind data={data} className={className} />;
}


function DispatchByKind({ data, className }: { data: WebMediaResource; className?: string }) {
  switch (data.kind) {
    case 'image':
      // Multi-source variants render as <picture>; single src as <img>.
      if (data.sources.length > 0) {
        return (
          <picture className={className}>
            {data.sources.map((s, i) => (
              <source key={i} srcSet={s.src} type={s.type ?? undefined} media={s.media ?? undefined} />
            ))}
            <img
              src={data.src ?? data.sources[0].src}
              width={data.width ?? undefined}
              height={data.height ?? undefined}
              alt={data.alt}
              loading="lazy"
            />
          </picture>
        );
      }
      return (
        <img
          src={data.src ?? ''}
          width={data.width ?? undefined}
          height={data.height ?? undefined}
          alt={data.alt}
          className={className}
          loading="lazy"
        />
      );

    case 'video':
      return (
        <video
          controls={data.controls}
          autoPlay={data.autoplay}
          loop={data.loop}
          // Browsers block unmuted autoplay outright — muted only when
          // actually autoplaying, so a controls=true explainer video
          // (autoplay=false) starts with real sound available.
          muted={data.autoplay}
          playsInline
          poster={data.poster ?? undefined}
          width={data.width ?? undefined}
          height={data.height ?? undefined}
          aria-label={data.alt}
          className={className}
        >
          {data.sources.length > 0
            ? data.sources.map((s, i) => (
                <source key={i} src={s.src} type={s.type ?? undefined} media={s.media ?? undefined} />
              ))
            : <source src={data.src ?? ''} />
          }
        </video>
      );

    case 'svg':
      // Inline-rendered as <object> so animated SMIL inside still
      // works; falls back to <img> if the browser can't render.
      return (
        <object
          data={data.src ?? ''}
          type="image/svg+xml"
          width={data.width ?? undefined}
          height={data.height ?? undefined}
          aria-label={data.alt}
          className={className}
        >
          <img src={data.src ?? ''} alt={data.alt} />
        </object>
      );

    case 'lottie':
      // Lottie playback library not yet wired — pilot stub. When a
      // real Lottie asset lands, install lottie-react and render here.
      return (
        <div className={cn('flex items-center justify-center text-muted-foreground border border-dashed border-border/40 rounded-lg', className)}>
          <span className="text-xs">Lottie renderer not yet wired</span>
        </div>
      );

    case 'react_component': {
      const Component = data.component_name
        ? REACT_MEDIA_REGISTRY[data.component_name]
        : null;
      if (!Component) {
        return (
          <div className={cn('flex flex-col items-center justify-center text-muted-foreground', className)}>
            <ImageOff className="h-6 w-6 opacity-40 mb-2" />
            <span className="text-xs">Unknown component: {data.component_name ?? '(none)'}</span>
          </div>
        );
      }
      return (
        <div className={className} aria-label={data.alt}>
          <Component />
        </div>
      );
    }

    default: {
      // Exhaustive switch — TS will flag if a new kind is added without a case.
      const _exhaustive: never = data.kind;
      return null;
    }
  }
}
